import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { StreamChat } from 'stream-chat';
import { getStreamChatToken, getUserSettings } from '../utils/api';
import { checkPushSubscription } from '../utils/notificationService';
import { ChatPresenceContext } from './ChatPresenceContext';

const StreamChatContext = createContext();

export const useStreamChat = () => useContext(StreamChatContext);

export const StreamChatProvider = ({ children }) => {
  const [chatClient, setChatClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [user, setUser] = useState(null);
  const [userPresence, setUserPresence] = useState({});
  const initTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 5;
  const unreadCountIntervalRef = useRef(null);
  const chatClientRef = useRef(null);

  // Make the Stream client globally available for components that need direct access
  useEffect(() => {
    if (chatClient) {
      window.streamChatClient = chatClient;
    }
    return () => {
      // Clean up global reference when component unmounts
      if (window.streamChatClient === chatClient) {
        window.streamChatClient = null;
      }
    };
  }, [chatClient]);

  // ---- START: Unread Count Logic ----
  const fetchUnreadCount = useCallback(async () => {
    if (!chatClient || !user) {
      setUnreadMessageCount(0);
      return;
    }

    try {
      // Get unread count from Stream Chat
      const filters = { type: 'messaging', members: { $in: [user.id] } };
      const channels = await chatClient.queryChannels(filters, {}, { limit: 30 });
      
      let totalUnread = 0;
      channels.forEach(channel => {
        if (channel.countUnread) {
          totalUnread += channel.countUnread();
        }
      });
      

      setUnreadMessageCount(totalUnread);
    } catch (error) {
      console.error('[StreamChatContext] Error getting total unread message count:', error);
      setUnreadMessageCount(0);
    }
  }, [chatClient, user]);

  // Poll for unread count and setup real-time handler
  useEffect(() => {
    if (chatClient && user) {

      // Initial fetch
      fetchUnreadCount();

      // Fallback poll (every 10 seconds) behind the real-time events below.
      // A background tab skips it and catches up the moment it is shown.
      if (unreadCountIntervalRef.current) clearInterval(unreadCountIntervalRef.current);
      unreadCountIntervalRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') fetchUnreadCount();
      }, 10000);
      const handleVisible = () => {
        if (document.visibilityState === 'visible') fetchUnreadCount();
      };
      document.addEventListener('visibilitychange', handleVisible);

      // Set up real-time event handlers
      const handleNewMessage = (event) => { 
        fetchUnreadCount(); 
        
        // Trigger notification if message is not from current user
        if (event.message?.user?.id !== user?.id) {
          // Dispatch global notification event
          window.dispatchEvent(new CustomEvent('showNotification', {
            detail: {
              type: 'chat-message',
              data: {
                message: event.message.text || 'New message',
                sender: {
                  id: event.message.user.id,
                  name: event.message.user.name || 'Unknown User'
                },
                channel: event.channel,
                messageData: event.message
              }
            }
          }));
        }
      };
      const handleMessageUpdated = () => { fetchUnreadCount(); };
      const handleMessageDeleted = () => { fetchUnreadCount(); };
      const handleChannelUpdated = () => { fetchUnreadCount(); };
      const handleNotificationMarkRead = () => { fetchUnreadCount(); };

      chatClient.on('message.new', handleNewMessage);
      chatClient.on('message.updated', handleMessageUpdated);
      chatClient.on('message.deleted', handleMessageDeleted);
      chatClient.on('channel.updated', handleChannelUpdated);
      chatClient.on('notification.mark_read', handleNotificationMarkRead);

      // Set up presence tracking
      const handleUserPresenceChanged = (event) => {

        setUserPresence(prev => ({
          ...prev,
          [event.user.id]: {
            online: event.user.online,
            last_active: event.user.last_active
          }
        }));
      };

      const handleUserUpdated = (event) => {

        if (event.user && event.user.id) {
          setUserPresence(prev => ({
            ...prev,
            [event.user.id]: {
              online: event.user.online,
              last_active: event.user.last_active
            }
          }));
        }
      };

      chatClient.on('user.presence.changed', handleUserPresenceChanged);
      chatClient.on('user.updated', handleUserUpdated);

      // Fetch initial presence data for users in channels
      const fetchInitialPresence = async () => {
        try {

          // Get all channels to find users we need presence for
          const channels = await chatClient.queryChannels(
            { type: 'messaging', members: { $in: [chatClient.user?.id] } },
            {},
            { limit: 30 }
          );
          
          // Collect all unique user IDs from channels
          const userIds = new Set();
          channels.forEach(channel => {
            Object.values(channel.state.members || {}).forEach(member => {
              if (member.user?.id && member.user.id !== chatClient.user?.id) {
                userIds.add(member.user.id);
              }
            });
          });
          
          // Query presence for these users
          if (userIds.size > 0) {
            const userIdsArray = Array.from(userIds);

            const response = await chatClient.queryUsers(
              { id: { $in: userIdsArray } },
              {},
              { presence: true }
            );
            

            // Update presence state with fetched data
            const presenceData = {};
            response.users.forEach(user => {
              presenceData[user.id] = {
                online: user.online || false,
                last_active: user.last_active
              };
            });
            
            setUserPresence(prev => ({
              ...prev,
              ...presenceData
            }));
            

          }
        } catch (error) {
          console.error('[StreamChatContext] Error fetching initial presence:', error);
        }
      };

      // Fetch presence data after a short delay to let the connection settle
      setTimeout(fetchInitialPresence, 1000);

      // Cleanup function
      return () => {

        if (unreadCountIntervalRef.current) {
          clearInterval(unreadCountIntervalRef.current);
          unreadCountIntervalRef.current = null;
        }
        
        chatClient.off('message.new', handleNewMessage);
        chatClient.off('message.updated', handleMessageUpdated);
        chatClient.off('message.deleted', handleMessageDeleted);
        chatClient.off('channel.updated', handleChannelUpdated);
        chatClient.off('notification.mark_read', handleNotificationMarkRead);
        chatClient.off('user.presence.changed', handleUserPresenceChanged);
        chatClient.off('user.updated', handleUserUpdated);
        document.removeEventListener('visibilitychange', handleVisible);
      };
    } else {
      // Cleanup if client disconnects or isn't ready
      if (unreadCountIntervalRef.current) {
        clearInterval(unreadCountIntervalRef.current);
        unreadCountIntervalRef.current = null;
      }
      setUnreadMessageCount(0);
    }
  }, [chatClient, user, fetchUnreadCount]);
  // ---- END: Unread Count Logic ----

  // The actual connection logic with retry mechanism
  const connectChat = async (userEmail, userSettings = null, retryCount = 0) => {

    if (retryCount > MAX_RETRIES) {
      console.error(`[StreamChatContext] Max retries (${MAX_RETRIES}) reached for connecting chat`);
      setError(new Error(`Failed to connect to chat after ${MAX_RETRIES} attempts. Please refresh the page or try again later.`));
      setLoading(false);
      return;
    }

    try {
      // Validate email
      if (!userEmail || !userEmail.includes('@')) {
        throw new Error('Valid user email is required for Stream Chat authentication');
      }

      // If there's an existing chat client, disconnect it first
      if (chatClient) {

        try {
          await chatClient.disconnectUser();

        } catch (disconnectError) {
          console.warn('[StreamChatContext] Error disconnecting chat client:', disconnectError);
        }
      }

      // If user settings not provided, try to fetch them using the email
      if (!userSettings) {
        try {

          userSettings = await getUserSettings(userEmail);

        } catch (error) {
          console.warn('[StreamChatContext] Error fetching user settings:', error);
          // Continue with default values
          userSettings = {};
        }
      }
      
      // Get display name
      let userDisplayName = userEmail;
      
      if (userSettings) {
        if (userSettings.firstName && userSettings.lastName) {
          userDisplayName = `${userSettings.firstName} ${userSettings.lastName}`;

        }
      }
      

      const response = await getStreamChatToken(userEmail);

      const { token, apiKey, userId: chatUserId } = response;
      
      // Check if we have a valid token response
      if (!token || !apiKey || !chatUserId) {
        console.error('[StreamChatContext] Invalid token response:', { token: !!token, apiKey: !!apiKey, chatUserId: !!chatUserId });
        throw new Error('Invalid token response from server');
      }
      
      // Initialize Stream Chat client

      const client = StreamChat.getInstance(apiKey);
      
      // Prepare user object
      const streamUser = {
        id: chatUserId,
        name: userDisplayName,
        email: userEmail,
      };

      // Add profile picture if available
      if (userSettings && userSettings.profilePictureThumb) {
        streamUser.image = userSettings.profilePictureThumb;
      }

      // Connect user
      await client.connectUser(streamUser, token);
      

      // Set up connection state handling
      client.on('connection.changed', (event) => {

        if (event.online === false) {

        }
      });

      // Set up error handling
      client.on('connection.error', (error) => {
        console.error('[StreamChatContext] Connection error:', error);
        setError(error);
      });

      setChatClient(client);
      chatClientRef.current = client; // Store in ref for cleanup
      setUser(streamUser);
      setUserId(chatUserId);
      setError(null);
      retryCountRef.current = 0; // Reset retry count on success
      

    } catch (error) {
      console.error('[StreamChatContext] Error connecting to Stream Chat:', error);
      setError(error);
      
      // Retry if not at max retries
      if (retryCount < MAX_RETRIES) {
        const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Exponential backoff, max 10s

        setTimeout(() => {
          connectChat(userEmail, userSettings, retryCount + 1);
        }, retryDelay);
      } else {
        setError(new Error(`Failed to connect to chat after ${MAX_RETRIES} attempts: ${error.message}`));
      }
    } finally {
      setLoading(false);
    }
  };

  // Main initialization function
  const initializeChat = useCallback(async (userEmail, userSettings = null) => {

    if (!userEmail || !userEmail.includes('@')) {
      console.error('[StreamChatContext] Valid email is required for chat initialization');
      return;
    }

    // Clear any existing timeout
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }

    setLoading(true);
    setError(null);
    retryCountRef.current = 0;

    // Add a slight delay to prevent rapid re-initialization
    initTimeoutRef.current = setTimeout(() => {
      connectChat(userEmail, userSettings);
    }, 500);
    
  }, []);

  // Disconnect function
  const disconnect = useCallback(async () => {

    // Clear intervals and timeouts
    if (unreadCountIntervalRef.current) {
      clearInterval(unreadCountIntervalRef.current);
      unreadCountIntervalRef.current = null;
    }
    
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }

    // Disconnect from Stream Chat
    if (chatClient) {
      try {
        await chatClient.disconnectUser();

      } catch (error) {
        console.error('[StreamChatContext] Error disconnecting from Stream Chat:', error);
      }
    }

    // Reset state
    setChatClient(null);
    chatClientRef.current = null; // Clear ref
    setUser(null);
    setUserId(null);
    setUnreadMessageCount(0);
    setUserPresence({});
    setError(null);
    setLoading(false);
    retryCountRef.current = 0;
  }, [chatClient]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      // Use ref to avoid dependency issues
      if (chatClientRef.current) {

        chatClientRef.current.disconnectUser().catch(error => {
          console.error('[StreamChatContext] Error disconnecting on unmount:', error);
        });
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Helper function to get user online status
  const getUserOnlineStatus = useCallback((userId) => {
    if (!userId || !userPresence[userId]) {
      return { online: false, lastSeen: null };
    }
    
    const presence = userPresence[userId];
    const isOnline = presence.online;
    const lastSeen = presence.last_active ? new Date(presence.last_active) : null;
    
    return { online: isOnline, lastSeen };
  }, [userPresence]);

  // Function to refresh presence data for specific users
  const refreshUserPresence = useCallback(async (userIds) => {
    if (!chatClient || !userIds || userIds.length === 0) return;
    
    try {

      const response = await chatClient.queryUsers(
        { id: { $in: userIds } },
        {},
        { presence: true }
      );
      

      // Update presence state with fetched data
      const presenceData = {};
      response.users.forEach(user => {
        presenceData[user.id] = {
          online: user.online || false,
          last_active: user.last_active
        };
      });
      
      setUserPresence(prev => ({
        ...prev,
        ...presenceData
      }));
      

    } catch (error) {
      console.error('[StreamChatContext] Error refreshing user presence:', error);
    }
  }, [chatClient]);

  // Memoized so the app shell (unread badge consumers) re-renders only when
  // these change; presence lives in its own context (ChatPresenceContext).
  const value = useMemo(() => ({
    chatClient,
    user,
    userId,
    loading,
    error,
    unreadMessageCount,
    refreshUserPresence,
    initializeChat,
    disconnect,
    fetchUnreadCount
  }), [chatClient, user, userId, loading, error, unreadMessageCount, refreshUserPresence, initializeChat, disconnect, fetchUnreadCount]);

  const presenceValue = useMemo(() => ({ userPresence, getUserOnlineStatus }), [userPresence, getUserOnlineStatus]);

  return (
    <StreamChatContext.Provider value={value}>
      <ChatPresenceContext.Provider value={presenceValue}>
        {children}
      </ChatPresenceContext.Provider>
    </StreamChatContext.Provider>
  );
}; 
